// ===================================================================
// WORKFLOWS — All Instances (History View)
// Table of every workflow instance across all statuses
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text, Paper, Group, Badge, Select, TextInput, Table,
  Progress, Center, Loader, SimpleGrid, SegmentedControl,
} from '../../components/gab/GabComponents';
import { IconSearch, IconBolt } from '@tabler/icons-react';
import { useWorkflowStore } from '../../store/workflowStore';
import type { InstanceStatus, WorkflowPriority } from '../../types/workflow';
import { INSTANCE_STATUS_COLORS, PRIORITY_COLORS } from '../../types/workflow';
import { fmtDate } from '../../lib/dateFormat';

// ── Constants ───────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'all',       label: 'All Statuses' },
  { value: 'active',    label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'paused',    label: 'Paused' },
];

// ── Helpers ─────────────────────────────────────────────────────

function progressPct(cur: string, total: string): number {
  const c = Number(cur) || 0;
  const t = Number(total) || 1;
  return Math.round((c / t) * 100);
}

function isManualFire(contextJson: string): boolean {
  if (!contextJson) return false;
  try {
    const ctx = JSON.parse(contextJson);
    return ctx?.manual === true;
  } catch { return false; }
}

// ── Component ───────────────────────────────────────────────────

export default function WorkflowAll() {
  const navigate = useNavigate();
  const { templates, instances, loading, loadAll } = useWorkflowStore();

  const [search, setSearch]                 = useState('');
  const [statusFilter, setStatusFilter]     = useState<string>('all');
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [moduleFilter, setModuleFilter]     = useState<string>('all');
  const [sourceFilter, setSourceFilter]     = useState<string>('all');

  useEffect(() => { loadAll(); }, []);

  // ── Filter options ────────────────────────────────────────────

  const templateOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [{ value: 'all', label: 'All Templates' }];
    for (const t of templates) {
      const v = String((t as any).id ?? (t as any).Rid ?? '');
      if (!v || v === '0' || v === 'undefined' || seen.has(v)) continue;
      seen.add(v);
      opts.push({ value: v, label: (t as any).name || (t as any).templateName || 'Unnamed' });
    }
    return opts;
  }, [templates]);

  const moduleOptions = useMemo(() => {
    const seen = new Set<string>(['all']);
    const opts: { value: string; label: string }[] = [{ value: 'all', label: 'All Modules' }];
    for (const i of instances) {
      const m = String(i.sourceModule || '');
      if (!m || m === 'undefined' || m === 'null' || seen.has(m)) continue;
      seen.add(m);
      opts.push({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) });
    }
    return opts;
  }, [instances]);

  // ── Filtered + sorted ────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return instances
      .filter(i => statusFilter   === 'all' || i.status       === statusFilter)
      .filter(i => templateFilter === 'all' || String(i.templateId) === templateFilter)
      .filter(i => moduleFilter   === 'all' || i.sourceModule === moduleFilter)
      .filter(i => {
        if (sourceFilter === 'all') return true;
        const manual = isManualFire(i.contextJson);
        return sourceFilter === 'manual' ? manual : !manual;
      })
      .filter(i => !q || (i.templateName || '').toLowerCase().includes(q)
        || (i.sourceRecordLabel || '').toLowerCase().includes(q)
        || (i.startedBy || '').toLowerCase().includes(q))
      .sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
  }, [instances, search, statusFilter, templateFilter, moduleFilter, sourceFilter]);

  // ── Stats ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    let manual = 0, last24h = 0, active = 0;
    for (const i of instances) {
      if (isManualFire(i.contextJson)) manual++;
      if (i.startedAt && new Date(i.startedAt).getTime() >= dayAgo) last24h++;
      if (i.status === 'active') active++;
    }
    return { total: instances.length, manual, last24h, active };
  }, [instances]);

  // ── Render ────────────────────────────────────────────────────

  if (loading && instances.length === 0) {
    return <Center py="xl"><Loader /></Center>;
  }

  return (
    <div>
      {/* Stat cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--color-primary-700)' }}>
          <Text size="xs" c="dimmed">Total Fires</Text>
          <Text fz={28} fw={700}>{stats.total}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #07963F' }}>
          <Text size="xs" c="dimmed">Active</Text>
          <Text fz={28} fw={700} c="#07963F">{stats.active}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
          <Text size="xs" c="dimmed">Manual Fires</Text>
          <Text fz={28} fw={700} c="#d97706">{stats.manual}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--color-info)' }}>
          <Text size="xs" c="dimmed">Last 24h</Text>
          <Text fz={28} fw={700} c="var(--color-info)">{stats.last24h}</Text>
        </Paper>
      </SimpleGrid>

      {/* Search + Filters */}
      <Group mb="md" gap="sm">
        <TextInput
          placeholder="Search workflows..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          w={220}
        />
        <Select
          data={STATUS_OPTIONS}
          value={statusFilter}
          onChange={v => setStatusFilter(v ?? 'all')}
          size="xs"
          w={160}
          placeholder="All Statuses"
        />
        <Select
          data={templateOptions}
          value={templateFilter}
          onChange={v => setTemplateFilter(v ?? 'all')}
          size="xs"
          w={220}
          placeholder="All Templates"
        />
        <Select
          data={moduleOptions}
          value={moduleFilter}
          onChange={v => setModuleFilter(v ?? 'all')}
          size="xs"
          w={160}
          placeholder="All Modules"
        />
        <SegmentedControl size="xs" value={sourceFilter} onChange={setSourceFilter}
          data={[
            { label: 'All', value: 'all' },
            { label: 'Auto', value: 'auto' },
            { label: `Manual${stats.manual > 0 ? ` (${stats.manual})` : ''}`, value: 'manual' },
          ]} />
      </Group>

      {/* Table */}
      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text c="dimmed" ta="center">No workflow instances found.</Text>
        </Paper>
      ) : (
        <Paper radius="sm" withBorder>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
          <Table striped highlightOnHover>
            <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
              <Table.Tr>
                <Table.Th>Template</Table.Th>
                <Table.Th>Record</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Progress</Table.Th>
                <Table.Th>Priority</Table.Th>
                <Table.Th>Started</Table.Th>
                <Table.Th>Completed</Table.Th>
                <Table.Th>Started By</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map(inst => {
                const pct      = progressPct(String(inst.currentStepIndex ?? 0), String(inst.totalSteps));
                const status   = inst.status as InstanceStatus;
                const priority = inst.priority as WorkflowPriority;
                const statusColor   = INSTANCE_STATUS_COLORS[status] ?? '#7B7D7F';
                const priorityColor = PRIORITY_COLORS[priority] ?? '#7B7D7F';
                const isDone = status === 'completed' || status === 'cancelled';

                return (
                  <Table.Tr
                    key={inst.Rid}
                    style={{ cursor: 'pointer', opacity: isDone ? 0.75 : 1 }}
                    onClick={() => navigate(`/workflows/instance/${inst.Rid}`)}
                  >
                    {/* Template + module */}
                    <Table.Td>
                      <Text size="sm" fw={500}>{inst.templateName || '—'}</Text>
                      {inst.sourceModule && (
                        <Text size="xs" c="dimmed">{inst.sourceModule}</Text>
                      )}
                    </Table.Td>

                    {/* Source record */}
                    <Table.Td>
                      <Text size="sm" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inst.sourceRecordLabel || '—'}
                      </Text>
                    </Table.Td>

                    {/* Status badge */}
                    <Table.Td>
                      <Badge
                        size="xs"
                        variant="light"
                        style={{ backgroundColor: statusColor + '22', color: statusColor }}
                        tt="capitalize"
                      >
                        {inst.status || '—'}
                      </Badge>
                    </Table.Td>

                    {/* Progress */}
                    <Table.Td style={{ minWidth: 120 }}>
                      <Group gap={6} wrap="nowrap">
                        <Progress
                          value={pct}
                          size="xs"
                          radius="xl"
                          style={{ flex: 1 }}
                          color={pct === 100 ? 'var(--color-success)' : '#0284C7'}
                        />
                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {inst.currentStepIndex}/{inst.totalSteps}
                        </Text>
                      </Group>
                    </Table.Td>

                    {/* Priority */}
                    <Table.Td>
                      <Badge
                        size="xs"
                        variant="light"
                        style={{ backgroundColor: priorityColor + '22', color: priorityColor }}
                        tt="capitalize"
                      >
                        {inst.priority || 'normal'}
                      </Badge>
                    </Table.Td>

                    {/* Started */}
                    <Table.Td>
                      <Text size="xs" c="dimmed">{fmtDate(inst.startedAt)}</Text>
                    </Table.Td>

                    {/* Completed */}
                    <Table.Td>
                      <Text size="xs" c="dimmed">{fmtDate(inst.completedAt)}</Text>
                    </Table.Td>

                    {/* Started by */}
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Text size="xs">{inst.startedBy || '—'}</Text>
                        {isManualFire(inst.contextJson) && (
                          <Badge size="xs" color="#F86A0B" variant="light" leftSection={<IconBolt size={9} />}>
                            Manual
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </div>
        </Paper>
      )}
    </div>
  );
}
